/**
 * Simply Food Storage cart-integrated Save for Later. v1.0.0
 *
 * Saved lines remain browser-local. Cart-origin records retain Shopify line-item
 * properties so custom buckets can be restored without rebuilding their data.
 */
(() => {
  const storageKey = 'saved_for_later';

  function readSavedItems() {
    try {
      const items = JSON.parse(localStorage.getItem(storageKey));
      return Array.isArray(items) ? items.filter((item) => item && item.id) : [];
    } catch (error) {
      console.error('Unable to read saved-for-later items.', error);
      return [];
    }
  }

  function writeSavedItems(items) {
    localStorage.setItem(storageKey, JSON.stringify(items));
  }

  function recordKey(item) {
    return String(item.storage_key || item.key || `product_${item.id}`);
  }

  function normalizeImage(item) {
    if (item.image && typeof item.image === 'string') return item.image;
    if (item.featured_image && item.featured_image.url) return item.featured_image.url;
    if (Array.isArray(item.images) && item.images.length) return item.images[0];
    return '';
  }

  function normalizeProperties(item) {
    if (item.properties && typeof item.properties === 'object') return item.properties;

    const properties = {};
    if (item.bundle) properties.Custom_Bucket = item.bundle;
    (item.props || []).forEach((value, index) => {
      properties[`Pouch ${index + 1}`] = value;
    });
    (item.handles || []).forEach((value, index) => {
      properties[`_product_url_${index + 1}`] = value;
    });
    (item.images || []).forEach((value, index) => {
      properties[`_product_image_${index + 1}`] = value;
    });
    return properties;
  }

  function money(item) {
    if (Number.isFinite(Number(item.final_price))) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(Number(item.final_price) / 100);
    }
    const price = String(item.price || '0').replace(/[^0-9.-]/g, '');
    return `$${Number(price || 0).toFixed(2)}`;
  }

  function announce(message, isError = false) {
    if (window.alertify) {
      window.alertify[isError ? 'error' : 'success'](message);
    }
  }

  function createSavedCard(item) {
    const card = document.createElement('article');
    card.className = 'sfs-saved-item';
    card.dataset.savedKey = recordKey(item);

    const image = normalizeImage(item);
    const properties = normalizeProperties(item);
    const title = properties.Custom_Bucket || item.product_title || item.title || 'Saved item';
    const quantity = Math.max(1, Number(item.quantity) || 1);

    if (image) {
      const imageLink = document.createElement('a');
      imageLink.className = 'sfs-saved-item__image-link';
      imageLink.href = item.url || '#';
      const img = document.createElement('img');
      img.className = 'sfs-saved-item__image';
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      imageLink.appendChild(img);
      card.appendChild(imageLink);
    }

    const content = document.createElement('div');
    content.className = 'sfs-saved-item__content';
    const heading = document.createElement('h3');
    heading.className = 'sfs-saved-item__title';
    if (item.url) {
      const link = document.createElement('a');
      link.href = item.url;
      link.textContent = title;
      heading.appendChild(link);
    } else {
      heading.textContent = title;
    }
    content.appendChild(heading);

    const meta = document.createElement('p');
    meta.className = 'sfs-saved-item__meta';
    meta.textContent = `${money(item)}${quantity > 1 ? ` · Qty ${quantity}` : ''}`;
    content.appendChild(meta);

    const actions = document.createElement('div');
    actions.className = 'sfs-saved-item__actions';
    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'sfs-saved-item__add sfs-btn-solid';
    addButton.dataset.savedAction = 'add';
    addButton.textContent = 'ADD TO CART';
    actions.appendChild(addButton);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'sfs-saved-item__remove';
    removeButton.dataset.savedAction = 'remove';
    removeButton.textContent = 'Remove';
    actions.appendChild(removeButton);
    content.appendChild(actions);
    card.appendChild(content);
    return card;
  }

  function renderSavedItems() {
    const mount = document.querySelector('[data-sfs-saved-for-later-mount]');
    if (!mount) return;

    mount.replaceChildren();
    const items = readSavedItems();
    if (!items.length) return;

    const section = document.createElement('section');
    section.className = 'sfs-saved';
    section.setAttribute('aria-labelledby', 'sfs-saved-title');
    const heading = document.createElement('h2');
    heading.id = 'sfs-saved-title';
    heading.className = 'sfs-saved__title';
    heading.textContent = 'Saved for Later';
    section.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'sfs-saved__grid';
    items.forEach((item) => grid.appendChild(createSavedCard(item)));
    section.appendChild(grid);
    mount.appendChild(section);
  }

  async function saveCartLine(button) {
    const card = button.closest('.product-card');
    const dataElement = card?.querySelector('[data-sfs-cart-line]');
    if (!dataElement) return;

    let line;
    try {
      line = JSON.parse(dataElement.textContent);
    } catch (error) {
      announce('This item could not be saved. Please try again.', true);
      return;
    }

    line.storage_key = line.key;
    const savedItems = readSavedItems();
    const wasAdded = !savedItems.some((item) => recordKey(item) === recordKey(line));
    if (wasAdded) {
      savedItems.push(line);
      writeSavedItems(savedItems);
    }

    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Saving…';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });
      if (!response.ok) throw new Error(`Cart change failed: ${response.status}`);
      window.location.reload();
    } catch (error) {
      if (wasAdded) {
        writeSavedItems(savedItems.filter((item) => recordKey(item) !== recordKey(line)));
      }
      button.removeAttribute('aria-disabled');
      button.textContent = 'Save for Later';
      announce('This item could not be saved. Please try again.', true);
      console.error(error);
    }
  }

  async function addSavedItem(item, button) {
    button.disabled = true;
    button.textContent = 'ADDING…';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(item.variant_id || item.id),
            quantity: Math.max(1, Number(item.quantity) || 1),
            properties: normalizeProperties(item),
          }],
        }),
      });
      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);
      writeSavedItems(readSavedItems().filter((saved) => recordKey(saved) !== recordKey(item)));
      window.location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'ADD TO CART';
      announce('This item is unavailable or could not be added.', true);
      console.error(error);
    }
  }

  document.addEventListener('click', (event) => {
    const saveButton = event.target.closest('.saved_button');
    if (saveButton) {
      event.preventDefault();
      if (saveButton.getAttribute('aria-disabled') === 'true') return;
      saveCartLine(saveButton);
      return;
    }

    const actionButton = event.target.closest('[data-saved-action]');
    if (!actionButton) return;
    const card = actionButton.closest('[data-saved-key]');
    const item = readSavedItems().find((saved) => recordKey(saved) === card?.dataset.savedKey);
    if (!item) return;

    if (actionButton.dataset.savedAction === 'remove') {
      writeSavedItems(readSavedItems().filter((saved) => recordKey(saved) !== recordKey(item)));
      renderSavedItems();
      return;
    }
    addSavedItem(item, actionButton);
  });

  document.addEventListener('DOMContentLoaded', renderSavedItems);
})();
